import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiRefreshCw, FiSearch, FiFileText } from "react-icons/fi";
import { shopClient, eurCents, fmtDate, type ShopQuoteListItem } from "./shopClient";

const STATUS_FILTERS: Array<{ key: string; label: string }> = [
	{ key: "", label: "Tous" },
	{ key: "New", label: "Nouveaux" },
	{ key: "Quoted", label: "Devisés" },
	{ key: "Won", label: "Gagnés" },
	{ key: "Lost", label: "Perdus" },
];

export const QUOTE_STATUS: Record<string, { label: string; cls: string }> = {
	New: { label: "Nouveau", cls: "bg-sky-50 text-sky-700" },
	Quoted: { label: "Devisé", cls: "bg-amber-50 text-amber-700" },
	Won: { label: "Gagné", cls: "bg-emerald-50 text-emerald-700" },
	Lost: { label: "Perdu", cls: "bg-slate-100 text-slate-600" },
};

/** B2B quote (devis) back-office list. Quotes arrive from the public Pro page. */
export default function Quotes(): React.ReactElement {
	const history = useHistory();
	const [items, setItems] = useState<ShopQuoteListItem[]>([]);
	const [status, setStatus] = useState("");
	const [search, setSearch] = useState("");
	const [query, setQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async (st: string, q: string) => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.quotes.list(st || undefined, q.trim() || undefined, 1, 100);
			setItems(res.items);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { void load(status, query); }, [load, status, query]);

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="text-2xl font-semibold text-slate-900">Devis (Pro)</h1>
						<p className="mt-1 text-sm text-slate-500">Demandes de devis multi-références des professionnels</p>
					</div>
					<button
						type="button"
						onClick={() => load(status, query)}
						disabled={loading}
						className="ml-auto inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
					>
						<FiRefreshCw className={loading ? "animate-spin" : ""} />
						Actualiser
					</button>
				</div>

				<div className="flex flex-wrap items-center gap-2">
					<div className="flex flex-wrap gap-1.5">
						{STATUS_FILTERS.map((f) => (
							<button
								key={f.key || "all"}
								type="button"
								onClick={() => setStatus(f.key)}
								className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${status === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"}`}
							>
								{f.label}
							</button>
						))}
					</div>
					<div className="relative ml-auto min-w-[16rem] flex-1">
						<FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
						<input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter") setQuery(search); }}
							placeholder="Entreprise, e-mail, nom…"
							className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</div>
				</div>

				{error && <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

				<div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-slate-100 text-sm">
							<thead className="bg-slate-50">
								<tr>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Entreprise</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Contact</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Réf.</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Statut</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Devisé</th>
									<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Date</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{loading ? (
									<tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Chargement…</td></tr>
								) : items.length === 0 ? (
									<tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400"><FiFileText className="mx-auto mb-2 h-6 w-6 text-slate-300" />Aucun devis</td></tr>
								) : (
									items.map((q) => {
										const s = QUOTE_STATUS[q.status] ?? { label: q.status, cls: "bg-slate-100 text-slate-600" };
										return (
											<tr key={q.id} onClick={() => history.push("/quotes/" + q.id)} className="cursor-pointer hover:bg-slate-50">
												<td className="px-5 py-3 font-medium text-slate-900">{q.company || "—"}</td>
												<td className="px-5 py-3 text-slate-600">{q.customerName || q.customerEmail || "—"}</td>
												<td className="px-5 py-3 text-slate-600">{q.lineCount}</td>
												<td className="px-5 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span></td>
												<td className="px-5 py-3 text-slate-600">{q.quotedTotalCents != null ? eurCents(q.quotedTotalCents) : "—"}</td>
												<td className="px-5 py-3 text-slate-600">{fmtDate(q.createdAt)}</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
}
